import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, FileText, Maximize2, Minus, Upload, X } from 'lucide-react'
import InfoTooltip from '../InfoTooltip.jsx'
import {
  addVerifiedEquityToPositionAssetList, createPositionAssetList,
  readPositionAssetListState, readPositionAssetListView, searchPositionAssetListInstruments, removePositionAssetListMembership,
} from '../../lib/positionAssetListStore.js'
import { importAssetListRows, importQuoteStatus, MAX_IMPORT_BYTES, parseAssetListCsv } from '../../lib/assetListCsvImport.js'
import { previewAssetListReplacement, applyAssetListReplacement } from '../../lib/assetListReplacement.js'
import { readImportFile, readPastedCsv, runArchivedAssetListImport, suggestCsvReportDate } from '../../lib/assetListImportHistory.js'
import { importRowProfile, importSectorSummary } from '../../lib/assetListImportProfiles.js'
import { assetListImportArchive } from '../../lib/assetListImportHistoryStore.js'
import { ibkrInstrumentQuoteStore } from '../../lib/marketData/ibkrInstrumentQuoteStore.js'

const api = {
  remove: removePositionAssetListMembership, add: addVerifiedEquityToPositionAssetList, readState: readPositionAssetListState,
  readView: readPositionAssetListView, search: (query, options) => searchPositionAssetListInstruments(query, { ...options, profile: true }),
}

function ImportClassification({ profile }) {
  return <><span>{profile.sector || 'Unavailable'}</span>{profile.category && <span className="mt-0.5 block text-xs" title={profile.subcategory || undefined}>{profile.category}</span>}</>
}

export default function AssetListImportModal({ lists, selectedListId, onClose, onImported }) {
  const [minimized, setMinimized] = useState(false)
  const [outcome, setOutcome] = useState(null)
  const [notice, setNotice] = useState(null)
  const [destination, setDestination] = useState(selectedListId || 'new')
  const [mode, setMode] = useState('merge')
  const [plan, setPlan] = useState(null)
  const [removedIds, setRemovedIds] = useState(new Set())
  const [replacementDone, setReplacementDone] = useState(false)
  const [removedCount, setRemovedCount] = useState(0)
  const [previewing, setPreviewing] = useState(false)
  const replacing = destination !== 'new' && mode === 'replace'
  const [name, setName] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [rows, setRows] = useState([])
  const [fileName, setFileName] = useState('')
  const [originalBase64, setOriginalBase64] = useState(null)
  const [reportDate, setReportDate] = useState('')
  const [error, setError] = useState(null)
  const [reading, setReading] = useState(false)
  const [running, setRunning] = useState(false)
  const [started, setStarted] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [quotes, setQuotes] = useState({})
  const dialog = useRef(null)
  const fileInput = useRef(null)
  const run = useRef(null)
  const mounted = useRef(true)
  const createdList = useRef(null)
  const releases = useRef(new Map())
  const fileGeneration = useRef(0)
  const busy = useRef(false)

  useEffect(() => {
    mounted.current = true
    const previous = document.activeElement
    dialog.current?.focus()
    return () => {
      mounted.current = false
      run.current?.abort()
      fileGeneration.current += 1
      for (const release of releases.current.values()) release()
      releases.current.clear()
      previous?.focus?.()
    }
  }, [])

  useEffect(() => {
    if (minimized || started || running) return
    function paste(event) {
      if (event.defaultPrevented || busy.current) return
      const target = event.target
      if (target !== document && target !== document.body && !dialog.current?.contains(target)) return
      // Normal field paste still edits that field, rather than replacing the CSV.
      if (target.closest?.('input, textarea, [contenteditable]:not([contenteditable="false"])')) return
      if (receiveTransfer(event.clipboardData)) { event.preventDefault(); event.stopPropagation() }
    }
    document.addEventListener('paste', paste)
    return () => document.removeEventListener('paste', paste)
  }, [minimized, started, running])

  useEffect(() => { dialog.current?.focus() }, [minimized])

  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(() => setNotice(null), 8000)
    return () => clearTimeout(timer)
  }, [notice])

  function trapKeys(event) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      setMinimized(true)
    }
    if (event.key !== 'Tab') return
    const elements = [...dialog.current.querySelectorAll('button, input, select, [tabindex="0"]')].filter(element => !element.disabled && !element.hidden)
    const first = elements[0], last = elements.at(-1)
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
      event.preventDefault(); last?.focus()
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) {
      event.preventDefault(); first?.focus()
    }
  }

  function resetSource(name = '') {
    const generation = ++fileGeneration.current
    setOutcome(null); setNotice(null)
    setPlan(null); setReplacementDone(false); setRows([]); setError(null); setOriginalBase64(null); setFileName(name)
    setReportDate(suggestCsvReportDate(name)); setReading(false); setDragOver(false)
    if (fileInput.current) fileInput.current.value = ''
    return generation
  }

  async function loadFile(file) {
    if (!file || started || running || busy.current) return
    const generation = resetSource(file.name)
    setReading(true)
    try {
      if (!/\.csv$/i.test(file.name) && file.type !== 'text/csv') throw new Error('Choose a CSV file.')
      if (file.size > MAX_IMPORT_BYTES) throw new Error('Choose a CSV smaller than 1 MB.')
      const { text, originalBase64: original } = await readImportFile(file)
      if (!mounted.current || generation !== fileGeneration.current) return
      setRows(parseAssetListCsv(text))
      setOriginalBase64(original)
    } catch (failure) {
      if (mounted.current && generation === fileGeneration.current) setError(failure.message)
    } finally {
      if (mounted.current && generation === fileGeneration.current) setReading(false)
    }
  }

  function loadPaste(text) {
    resetSource()
    try {
      const source = readPastedCsv(text)
      setRows(parseAssetListCsv(source.text))
      setOriginalBase64(source.originalBase64); setFileName(source.fileName)
    } catch (failure) { setError(failure.message) }
  }

  function receiveTransfer(data) {
    if (!data || started || running || busy.current) return false
    const files = data.files?.length ? [...data.files]
      : [...(data.items || [])].filter(item => item.kind === 'file').map(item => item.getAsFile()).filter(Boolean)
    if (files.length > 1) { resetSource(); setError('Choose one CSV at a time.'); return true }
    if (files.length) { void loadFile(files[0]); return true }
    const text = data.getData?.('text/csv') || data.getData?.('text/plain')
    if (!text) return false
    loadPaste(text)
    return true
  }

  function updateRow(row) {
    if (!mounted.current) return
    setRows(current => current.map(item => item.id === row.id ? row : item))
    if (row.instrumentId && !releases.current.has(row.instrumentId)) {
      try {
        const release = ibkrInstrumentQuoteStore.retain(row.instrumentId, snapshot => {
          if (mounted.current) setQuotes(current => ({ ...current, [row.instrumentId]: snapshot }))
        })
        releases.current.set(row.instrumentId, release)
      } catch { /* Preserve saved membership even if quote observation fails. */ }
    }
  }

  async function startImport() {
    if (busy.current || !ready) return
    busy.current = true
    setMinimized(true)
    setOutcome(null); setNotice(null)
    setRunning(true); setStopping(false); setError(null)
    const controller = new AbortController()
    run.current = controller
    let completedOutcome = 'stopped'
    let refreshFailed = false
    let applicationStarted = false
    let replacementPlan = null
    let listId = createdList.current || (destination === 'new' ? null : destination)
    try {
      if (replacing) {
        setPreviewing(true); setPlan(null); setRemovedCount(0); setRemovedIds(new Set())
        for (const release of releases.current.values()) release()
        releases.current.clear(); setQuotes({})
        replacementPlan = await previewAssetListReplacement({ rows, listId, api, signal: controller.signal })
        if (mounted.current) { setPlan(replacementPlan); setPreviewing(false) }
      }
      if (controller.signal.aborted) return
      if (!listId) {
        listId = await createPositionAssetList(name.trim())
        createdList.current = listId
      }
      if (mounted.current) setStarted(true)
      if (!controller.signal.aborted) {
        applicationStarted = true
        const result = await runArchivedAssetListImport({
          source: { listId, fileName, originalBase64, reportDate, mode: destination === 'new' ? 'create' : mode, rows },
          archive: assetListImportArchive, signal: controller.signal, onRow: updateRow,
          execute: onRow => replacing
            ? applyAssetListReplacement({ plan: replacementPlan, api, signal: controller.signal, onRow,
              onRemoval: row => { if (mounted.current) { setRemovedCount(count => count + 1); setRemovedIds(ids => new Set([...ids, row.instrumentId])) } } })
            : importAssetListRows({ rows, listId, api, signal: controller.signal, onRow }),
        })
        completedOutcome = result.outcome
        if (mounted.current && replacing && result.outcome === 'completed') setReplacementDone(true)
        else if (mounted.current && replacing) {
          setPlan(null); setStarted(false)
          setError('List update was not confirmed complete. Saved changes remain; check Import history before retrying.')
        }
      }
    } catch (failure) {
      completedOutcome = controller.signal.aborted ? 'stopped' : 'failed'
      if (mounted.current) {
        setError(failure.message || 'Could not import list.')
        if (replacing) { setPlan(null); setStarted(false) }
      }
    } finally {
      if (mounted.current && listId && (applicationStarted || createdList.current)) {
        try { await onImported(listId) }
        catch { refreshFailed = true; if (mounted.current) setError('Import finished, but the list could not refresh. Reopen the list to check saved assets.') }
      }
      if (mounted.current) {
        setRows(current => current.map(row => ['resolving', 'subscribing'].includes(row.status)
          ? { ...row, status: 'queued', message: 'Stopped — resume to check membership' } : row))
        setOutcome(completedOutcome)
        setNotice(refreshFailed ? 'Import finished; list refresh needs attention.' : completedOutcome === 'completed' ? 'Import complete. Your list is ready to review.' : completedOutcome === 'stopped' ? 'Import stopped. Review saved changes.' : 'Import needs attention. Review the results.')
        setRunning(false); setPreviewing(false); setStopping(false)
      }
      busy.current = false
    }
  }

  const pending = rows.filter(row => ['queued', 'error'].includes(row.status)).length
  const saved = rows.filter(row => ['added', 'existing'].includes(row.status)).length
  const skipped = rows.filter(row => ['invalid', 'duplicate'].includes(row.status)).length
  const failed = rows.filter(row => row.status === 'error').length
  const invalid = rows.filter(row => row.status === 'invalid').length
  const needsAttention = Boolean(error || failed || invalid || ['partial', 'failed'].includes(outcome))
  const ready = Boolean(originalBase64) && !reading && !running && !replacementDone && (replacing ? rows.length > 0 : pending > 0) && (destination !== 'new' || name.trim())
  const complete = outcome === 'completed' && !running
  const jobStatus = running ? (previewing ? 'Preparing import…' : 'Importing…') : needsAttention ? 'Needs attention' : complete ? (replacing ? 'List updated' : 'Import complete') : outcome === 'stopped' ? 'Import stopped' : outcome ? 'Needs attention' : 'Ready to import'
  const addedIds = new Set(plan?.added.map(row => row.id) || [])
  const keptIds = new Set(plan?.kept.map(row => row.id) || [])
  const sectorSummary = importSectorSummary(rows, plan)
  const expanded = reading || rows.length > 0
  const inputClass = 'w-full rounded border border-border bg-canvas2 p-2 text-sm text-ink'
  const choiceClass = selected => `rounded border px-3 py-2 text-sm disabled:opacity-40 ${selected ? 'border-ink bg-ink text-canvas' : 'border-border bg-canvas2 text-inkMuted hover:text-ink'}`

  if (minimized) return createPortal(
    <div className="fixed bottom-4 left-4 z-[200] w-80 max-w-[calc(100vw-2rem)] text-ink" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
      {notice && <div role={needsAttention ? 'alert' : 'status'} className={`mb-2 flex items-start gap-2 rounded-lg border bg-canvas p-3 text-sm shadow-xl ${needsAttention ? 'border-red-500/50 text-red-700 dark:text-red-300' : 'border-border'}`}>
        {needsAttention ? <AlertTriangle size={16} className="mt-0.5 shrink-0" /> : complete && <CheckCircle2 size={16} className="mt-0.5 shrink-0" />}
        <span className="flex-1">{notice}</span>
        <button type="button" aria-label="Dismiss import notification" onClick={() => setNotice(null)}><X size={16} /></button>
      </div>}
      <section ref={dialog} tabIndex={-1} aria-label="CSV import job" className="rounded-xl border border-border bg-canvas p-4 shadow-xl outline-none">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold"><Upload size={16} />{jobStatus}</h2>
          <button type="button" aria-label="Restore import window" onClick={() => setMinimized(false)} className="rounded p-1 text-inkMuted hover:text-ink"><Maximize2 size={16} /></button>
        </div>
        <p className="mt-2 truncate text-xs text-inkMuted" title={fileName}>{fileName}</p>
        <p role="status" className="mt-2 text-sm">{previewing ? 'Verifying tickers…' : `${saved} of ${rows.length} saved · ${failed} failed · ${skipped} skipped`}</p>
        {running && <p className="mt-1 text-xs text-inkMuted">Keep this workspace open while importing.</p>}
        <div className="mt-3 flex justify-end gap-2">
          {running && <button type="button" disabled={stopping} onClick={() => { setStopping(true); run.current?.abort() }} className="rounded border border-border px-3 py-1.5 text-xs disabled:opacity-40">{stopping ? 'Stopping…' : 'Stop after current ticker'}</button>}
          <button type="button" onClick={() => setMinimized(false)} className={`flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs ${needsAttention ? 'border-red-600 bg-red-600 text-white hover:bg-red-700' : 'border-border'}`}>
            {needsAttention && <AlertTriangle size={14} aria-hidden="true" />}
            {needsAttention ? 'Needs attention' : running ? 'View progress' : 'Review results'}
          </button>
          {complete && <button type="button" onClick={onClose} className="rounded bg-ink px-3 py-1.5 text-xs text-canvas">Done</button>}
        </div>
      </section>
    </div>, document.body,
  )

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4" onClick={event => event.stopPropagation()}>
      <section ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="asset-import-title"
        onKeyDown={trapKeys} className={`flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-xl border border-border bg-canvas text-ink shadow-xl outline-none ${expanded ? 'max-w-6xl' : 'max-w-xl'}`}>
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-1">
            <h2 id="asset-import-title" className="flex items-center gap-2 text-lg font-semibold"><Upload size={18} /> Import list</h2>
            <InfoTooltip label="About list imports" placement="bottom" text="Import U.S. stocks and ETFs into a new or existing list. The original CSV and each import result are saved in Import history. Existing assets are reused." />
          </div>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Minimize import window" title="Minimize import window" onClick={() => setMinimized(true)} className="rounded p-1 text-inkMuted hover:text-ink"><Minus size={20} /></button>
          <button type="button" aria-label="Close CSV import" title="Minimize to bottom left" onClick={() => setMinimized(true)} className="rounded p-1 text-inkMuted hover:text-ink disabled:opacity-40"><X size={20} /></button>
          </div>
        </div>
        <div data-testid="import-scroll-body" className={`min-h-0 overflow-y-auto overscroll-contain ${expanded ? 'lg:grid lg:grid-cols-[20rem_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden' : ''}`}>
          <div className="min-w-0 p-5 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain">
        <div className="grid gap-3">
          <label className="text-sm">Destination
            <select aria-label="Import destination" value={destination} disabled={started || running} onChange={event => { setDestination(event.target.value); setPlan(null) }} className={inputClass}>
              <option value="new">Create a new list</option>
              {lists.map(list => <option key={list.listId} value={list.listId}>{list.name}</option>)}
            </select>
          </label>
          {destination === 'new' && <label className="text-sm">List name
            <input aria-label="Import list name" maxLength={48} value={name} disabled={started || running} onChange={event => { setName(event.target.value) }} className={inputClass} />
          </label>}
        </div>
        {destination !== 'new' && <div className="mt-3 text-sm">
          <div className="flex items-center gap-1">
            <span id="csv-import-mode-label">List update</span>
            <InfoTooltip label="List update help" placement="bottom" text="Add to watchlist keeps every current asset and adds missing tickers. Update watchlist matches the CSV: add new tickers, keep matches, and remove assets not in the CSV. Other lists and positions are unchanged. All incoming tickers must verify before removals begin." />
          </div>
          <div role="group" aria-labelledby="csv-import-mode-label" className="mt-1 grid gap-2">
            {[["merge", "Add to watchlist"], ["replace", "Update watchlist"]].map(([value, label]) =>
              <button key={value} type="button" aria-pressed={mode === value} disabled={started || running}
                onClick={() => { if (mode !== value) { setMode(value); setPlan(null) } }} className={choiceClass(mode === value)}>{label}</button>)}
          </div>
        </div>}
        <div className="mt-4 shrink-0 text-sm">
          <div className="flex items-center gap-1">
            <span id="csv-import-source-label">CSV</span>
            <InfoTooltip label="CSV format help" placement="bottom" text="Use a Ticker or Symbol column, or one ticker per row without a header. Maximum 500 rows and 1 MB. Each ticker must resolve to one verified U.S. stock or ETF. Uploaded files are saved unchanged. Pasted text is saved as Pasted CSV.csv in Import history." />
          </div>
          <div className="relative mt-1"
            onDragOver={event => { event.preventDefault(); if (!started && !running) setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={event => { event.preventDefault(); event.stopPropagation(); setDragOver(false); receiveTransfer(event.dataTransfer) }}>
            <button type="button" aria-label="Choose CSV" disabled={started || running} onClick={() => fileInput.current?.click()}
              className={`w-full rounded-lg border text-left transition-colors disabled:cursor-default disabled:opacity-50 ${fileName ? 'flex items-center gap-3 py-3 pl-3 pr-10' : 'border-dashed px-8 py-8 text-center'} ${dragOver ? 'border-ink bg-canvas2/50' : 'border-border hover:border-ink/40 hover:bg-canvas2/20'}`}>
              {fileName ? <FileText size={20} className="shrink-0 text-inkMuted" /> : <Upload size={28} className="mx-auto mb-2 text-inkMuted" />}
              <span className="min-w-0">
                <span className="block break-all text-sm text-ink">{fileName || 'Drop CSV here'}</span>
                {!fileName && <span className="mt-1 block text-xs text-inkMuted">drop, click, or paste (⌘V)</span>}
              </span>
            </button>
            {fileName && <button type="button" aria-label="Clear CSV" disabled={started || running} onClick={() => resetSource()}
              className="absolute right-2 top-2 rounded p-1 text-inkMuted hover:text-ink disabled:opacity-40"><X size={16} /></button>}
            <input ref={fileInput} id="csv-import-file" aria-label="CSV file" type="file" accept=".csv,text/csv" hidden disabled={started || running}
              onChange={event => void loadFile(event.target.files?.[0])} />
          </div>
        </div>
        <div className="mt-3 text-sm">
          <div className="flex items-center gap-1">
            <label htmlFor="csv-import-report-date">Report date (optional)</label>
            <InfoTooltip label="Report date help" placement="bottom" text="The report's source date is suggested from its filename. You can edit or clear it. Upload and application times are recorded automatically and separately." />
          </div>
          <input id="csv-import-report-date" aria-label="Report / as-of date" type="date" value={reportDate} disabled={started || running} onChange={event => setReportDate(event.target.value)} className={`${inputClass} mt-1`} />
        </div>
          </div>
          {expanded && <section aria-label="Import preview and results" className="min-w-0 border-t border-border p-5 overflow-x-auto lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:border-l lg:border-t-0">
            <h3 className="text-base font-semibold">{started || running || outcome ? 'Import results' : 'CSV preview'}</h3>
            {replacing && !plan && !running && <p className="mt-1 text-sm text-inkMuted">Import updates this list to match the CSV, including removing assets not in the file. Validation and saving run in the background.</p>}
        {error && <p role="alert" className="mt-3 text-sm text-ember">{error}</p>}
        {(reading || running || rows.length > 0) && <p role="status" className="my-3 text-sm text-inkMuted">{previewing ? 'Verifying tickers and preparing changes…' : reading ? 'Reading CSV…' : running ? `Importing ${fileName} — ${saved} saved, ${failed} failed, ${skipped} skipped` : `${rows.length} rows · ${saved} saved · ${failed} failed · ${skipped} skipped · ${pending} remaining`}</p>}
        {sectorSummary.total > 0 && <div aria-label="Sector overview" className="mb-3 flex flex-wrap items-center gap-2 text-xs text-inkMuted">
          <span>Incoming list · {sectorSummary.total} unique tickers</span>
          {sectorSummary.sectors.map(({ sector, count }) => <span key={sector} className="rounded border border-border px-2 py-1">{sector} · {count}</span>)}
          {sectorSummary.unavailable > 0 && <span>Sector unavailable · {sectorSummary.unavailable}</span>}
        </div>}
        {plan && <div className="mb-4 rounded-lg border border-border p-3 text-sm" aria-label="Replacement preview">
          <p className="font-semibold">{replacementDone ? `Completed changes: ${plan.added.length} added · ${plan.kept.length} kept · ${plan.removed.length} removed` : `Planned changes: ${plan.added.length} to add · ${plan.kept.length} to keep · ${plan.removed.length} to remove`}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5"><span>{replacementDone ? 'Added:' : 'To add:'}</span>{plan.added.length ? plan.added.map(row => <span key={row.id} className="rounded border border-green-500/40 px-1.5 py-0.5">{row.symbol}</span>) : 'None'}</div>
          <p className="mt-1 break-words">Keep: {plan.kept.map(row => row.symbol).join(', ') || 'None'}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5"><span>{replacementDone ? 'Removed:' : 'To remove:'}</span>{plan.removed.length ? plan.removed.map(row => <span key={row.instrumentId} className="rounded bg-red-500/10 px-1.5 py-0.5 text-red-700 dark:text-red-300">{row.symbol}</span>) : 'None'}</div>
          {(running || replacementDone) && <p className="mt-1">{removedCount} of {plan.removed.length} memberships removed</p>}
        </div>}
        {replacementDone && <p role="status" className="mb-3 text-sm">The list now matches the CSV.</p>}
        {rows.length > 0 && <div className="rounded border border-border">
          <table aria-label="Imported assets" className="w-full min-w-[700px] table-fixed text-left text-sm [&_td]:break-words">
            <thead className="sticky top-0 bg-canvas2"><tr><th className="w-[12%] p-2">Ticker</th><th className="w-[26%] p-2">Company</th><th className="w-[20%] p-2">Sector / category<InfoTooltip label="Sector source help" placement="bottom" text="IBKR industry and category, which may differ from GICS classifications. Category appears below sector; hover the category for its subcategory when available. Counts cover unique incoming CSV tickers, excluding removed assets. Missing classifications are counted as unavailable; they are never guessed." /></th><th className="w-[22%] p-2">Import</th><th className="w-[20%] p-2">Market data<InfoTooltip label="Market data status help" placement="bottom" text="Saved assets keep their subscriptions requested after this window closes. Waiting for a quote means membership is saved but live data has not arrived." /></th></tr></thead>
            <tbody>{rows.map(row => <tr key={row.id} className="border-t border-border">
              <td className="p-2 font-mono"><span className={row.status === 'added' || (row.status === 'queued' && addedIds.has(row.id)) ? 'rounded border border-green-500/40 px-1.5 py-0.5' : ''}>{row.symbol || '—'}</span></td>
              <td className="p-2 text-inkMuted">{importRowProfile(row, plan).companyName || 'Unavailable'}</td>
              <td className="p-2 text-inkMuted"><ImportClassification profile={importRowProfile(row, plan)} /></td>
              <td className={`p-2 ${['error', 'invalid'].includes(row.status) ? 'text-ember' : 'text-inkMuted'}`}>{row.status === 'queued' && plan ? addedIds.has(row.id) ? 'Will add' : keptIds.has(row.id) ? 'Will keep' : row.message : row.message}</td>
              <td className="p-2 text-inkMuted">{row.instrumentId ? importQuoteStatus(quotes[row.instrumentId]) : ['added', 'existing'].includes(row.status) ? 'Saved — quote status unavailable' : row.status === 'subscribing' ? 'Requesting subscription' : '—'}</td>
            </tr>)}
            {plan?.removed.map(row => <tr key={`removed-${row.instrumentId}`} className="border-t border-border bg-red-500/5">
              <td className="p-2 font-mono"><span className="rounded bg-red-500/10 px-1.5 py-0.5 text-red-700 dark:text-red-300">{row.symbol}</span></td>
              <td className="p-2 text-inkMuted">{importRowProfile(row).companyName || 'Unavailable'}</td>
              <td className="p-2 text-inkMuted"><ImportClassification profile={importRowProfile(row)} /></td>
              <td className="p-2 text-red-700 dark:text-red-300">{removedIds.has(row.instrumentId) ? 'Removed from list' : 'Will remove'}</td>
              <td className="p-2 text-inkMuted">—</td>
            </tr>)}</tbody>
          </table>
        </div>}
          </section>}
          {!expanded && error && <p role="alert" className="px-5 pb-4 text-sm text-ember">{error}</p>}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-4">
          {complete && <span role="status" className="mr-auto flex items-center gap-2 text-sm"><CheckCircle2 size={16} />{replacing ? 'List updated' : 'Import complete'}</span>}
          <InfoTooltip label="Applying changes help" text="Imports are saved in stages. Stopping or an error can leave some changes saved. Check Import history and the current list before retrying." />
          {running ? <button type="button" disabled={stopping} onClick={() => { setStopping(true); run.current?.abort() }} className="rounded border border-border px-3 py-2 text-sm disabled:opacity-40">{stopping ? 'Stopping…' : 'Stop after current ticker'}</button>
            : <button type="button" onClick={onClose} className={`rounded px-3 py-2 text-sm ${complete ? 'bg-ink text-canvas' : 'border border-border'}`}>{started ? 'Done' : 'Cancel'}</button>}
          {!complete && <button type="button" disabled={!ready} onClick={() => void startImport()} className="rounded bg-ink px-3 py-2 text-sm text-canvas disabled:opacity-40">{running ? (previewing ? 'Preparing import…' : 'Importing…') : started ? 'Retry / resume remaining' : 'Import'}</button>}
        </div>
      </section>
    </div>, document.body,
  )
}
